import { useEffect, useState } from 'react';
import { useDesignPreviewStore } from '@/stores/design-preview-store';
import { api } from '@/lib/api';

/** `?design_share=<token>` — a link someone was sent, opened at top level. */
export const DESIGN_SHARE_QUERY_PARAM = 'design_share';

/**
 * Storefront-side handler for a shared design preview link.
 *
 * The sandbox's own preview is an iframe fed by same-origin postMessage, which
 * a real visitor can never enter. This is the opposite case on purpose: someone
 * with no account, opening a top-level page from a link they were sent, who
 * should see the draft applied to the real storefront.
 *
 * The token is the whole authorisation. It is unguessable, expiring and
 * revocable (migration 219), and the endpoint behind it returns one draft's
 * theme and nothing else.
 *
 * ## Why the banner is not optional
 *
 * This renders unpublished work on the live domain, at a normal URL, with no
 * visual difference from the real site. Someone forwarded the link two steps
 * later has no way to know they are looking at a proposal rather than the shop
 * — and might reasonably act on it. The banner is the only thing that keeps a
 * preview from being mistaken for a launch.
 *
 * Renders nothing when the parameter is absent, which is every ordinary visit.
 */
export default function DesignSharePreview() {
  const setPreviewThemeConfig = useDesignPreviewStore((s) => s.setPreviewThemeConfig);
  const [name, setName] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const token = new URLSearchParams(window.location.search).get(DESIGN_SHARE_QUERY_PARAM);
    if (!token) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await api.get<{ name: string; themeConfig: unknown }>(
          `/design-share/${encodeURIComponent(token)}`,
        );
        if (cancelled) return;
        setPreviewThemeConfig((res.themeConfig as any) ?? null);
        setName(res.name);
      } catch {
        // Expired, revoked, or never existed — the endpoint deliberately does
        // not distinguish them, and neither does this. Say the link is dead and
        // leave the live site rendering normally underneath.
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      // Leaving the page must not strand the candidate theme in the store.
      setPreviewThemeConfig(null);
    };
  }, [setPreviewThemeConfig]);

  if (failed) {
    return (
      <div
        role="status"
        // dark-exempt: fixed notice bar, deliberately identical in both themes
        // so it reads as chrome rather than as part of the design being shown.
        className="fixed inset-x-0 top-0 z-[60] bg-stone-900 px-4 py-2 text-center text-sm text-white"
      >
        This design preview link has expired or been turned off. You are seeing the live site.
      </div>
    );
  }

  if (!name) return null;

  return (
    <div
      role="status"
      // dark-exempt: see above.
      className="fixed inset-x-0 top-0 z-[60] bg-stone-900 px-4 py-2 text-center text-sm text-white"
    >
      Design preview: <strong className="font-semibold">{name}</strong> — not published, and visible
      only to people with this link.
    </div>
  );
}
